<template>
  <div class="container category">
    <div class="category__title-container">
      <div class="category__title-left">
        <div class="category__header-button-wrapper"
             v-if="isDataFetched">
          <button
            @click.stop="cancelSortingAndFilters"
            class="category__clear-filters">
            <span>clear all filters and sorting</span>
            <span class="category__cross">&times;</span>
          </button>
        </div>

      </div>
      <div class="category__title-right"
           v-if="isDataFetched">
        <div class="category__categories">
          <span class="category__count">Categories: {{totalItems}}</span>
          <base-select
            class="category__grid-select"
            :options="paginationOptions"
            alignLeft
            :titleStyle="{'padding-left': '5px'}"
            :headerStyle="{}"
            showTopBorder
            shiftLabelRight
            v-model="pageItemsLimit"
            :selectStyle="{'max-height': '35px !important', width: '100px !important'}"
            @change="handleLimitChange"
          />
        </div>
        <button class="category__button" @click="openCreateModal()">
          Create category
        </button>
      </div>
    </div>

    <div class="common-table category__table">
      <div class="common-table__row common-table__header"
           v-if="isDataFetched">
        <div class="common-table__header-cell"
             v-for="column in categoryTableColumnsConfig"
             :key="column.property"
             :class="[`category__${column.property}`]">
          <base-multi-select-sortable-header
            v-if="column.property !== 'edit-button'"
            class="category__select-header"
            :column="column"
            :sort-config="sortConfig"
            :predefinedOptions="filterParams[column.property]"
            @sort="onHeaderCellClicked(column, $event)"
            @apply="filterColumn($event, column)"
            @cancel="cancelFilter($event, column)"
            :ref="sortableHeaderRefs[column.property]"
          />
        </div>
      </div>
      <template v-if="dataMissing">
        <div class="common-table__row no-data-block">
          No records found
        </div>
      </template>
      <div class="" v-else-if="!isDataFetched">
        <div class="category__loader">
          <base-loader/>
        </div>
      </div>
      <template v-else>
        <template v-for="category in paginatedCategories">
          <div :key="category.id" class="common-table__row">
            <div class="common-table__cell"
                 v-for="column in categoryTableColumnsConfig"
                 :key="column.property"
                 :class="[`category__${column.property}`, `category__row_${column.property}`]">
              <template v-if="column.property === 'edit-button'">
                <a @click="openCreateModal(category.id)" class="edit-icon">
                  <base-material-design-icon icon="edit"/>
                </a>
              </template>
              <template v-else-if="column.property === 'is_active'">
                  <span>
                    {{ category[column.property] ? 'Yes' : 'No' }}
                  </span>
              </template>
              <template v-else-if="column.property === 'name'">
                <base-truncate-label class="category__truncate"
                                     :label="category[column.property]"
                                     watchClass="category__truncate"/>
              </template>
              <template v-else>
                  <span>
                    {{ category[column.property] }}
                  </span>
              </template>
            </div>
          </div>
        </template>
      </template>
    </div>
    <div class="pagination-wrapper" v-show="hasPagination">
      <base-pagination :limit="pageItemsLimit"
                       :total-items="totalItems"
                       @pageChanged="pageChanged"/>
    </div>
    <div class="category__update-loader" v-if="isDataUpdating">
      <base-loader class="category__update-loader-ring"/>
    </div>

    <modal name="create"
           v-bind="changeCategoryModalProps">
      <CreateUpdateCategoryPopup
        ref="create-category"
        @apply="createOrUpdateCategory($event)"
        @delete="createOrUpdateCategory($event, true)"
        :selected-category="selectedCategory"
        @cancel="closeCreateModal"/>
    </modal>

  </div>
</template>

<script>
import helper from '@/services/Helper'
import { mapActions, mapState } from 'vuex'
import baseModalProps from '@/common/BaseModalProps'
import CreateUpdateCategoryPopup from './CreateUpdateCategoryPopup'
import _ from 'lodash'
import BaseSortMixins from '@/common/BaseSortMixins'

const ITEMS_PER_PAGE = 100
const EMPTY_FILTERS = {
  name: '',
  fk_pivot_table: [],
  fk_pivot_field: [],
  is_active: [],
  sort_column: [],
  sort_order: [],
  page_from: 0,
  page_to: ITEMS_PER_PAGE
}
const PAGINATION_OPTIONS = [
  {
    label: '100',
    value: 100
  },
  {
    label: '250',
    value: 250
  },
  {
    label: '500',
    value: 500
  }
]
// table config
const DEFAULT_CONFIG = [
  {
    label: '',
    property: 'edit-button'
  },
  {
    label: 'Name',
    property: 'name',
    isDefaultSort: true,
    isSmartInput: true
  },
  {
    label: 'Pivot Table',
    property: 'fk_pivot_table',
    isSelectableValue: true,
    isSmartInput: true,
    options: []
  },
  {
    label: 'Pivot Field',
    property: 'fk_pivot_field',
    isSmartInput: true
  },
  {
    label: 'Active',
    property: 'is_active',
    isSmartInput: false,
    options: []
  }
]

export default {
  name: 'Category',
  components: { CreateUpdateCategoryPopup },
  mixins: [BaseSortMixins],
  data () {
    return {
      isDataFetched: false,
      selectedCategory: null,
      isDataUpdating: false,
      filteredCategories: [],
      sortConfig: [],
      pageItemsLimit: ITEMS_PER_PAGE,
      totalItems: 0,
      categoryTableColumnsConfig: _.cloneDeep(DEFAULT_CONFIG),
      filterParams: _.cloneDeep(EMPTY_FILTERS),
      sortableHeaderRefs: {
        'name': 'name',
        'fk_pivot_table': 'fk_pivot_table',
        'fk_pivot_field': 'fk_pivot_field',
        'is_active': 'is_active'
      },
      paginationOptions: _.cloneDeep(PAGINATION_OPTIONS)
    }
  },
  created () {
    this.loadData()
  },
  watch: {
    settings: {
      deep: true,
      handler (settings) {
        const pivotFields = settings.pivot_fields.map(helper.convertListToOptions)
        this.categoryTableColumnsConfig.find(item => item.property === 'fk_pivot_field').options = pivotFields

        const pivotTables = settings.pivot_tables.map(helper.convertListToOptions)
        this.categoryTableColumnsConfig.find(item => item.property === 'fk_pivot_table').options = pivotTables

        const actives = settings.actives.map(helper.convertListToOptions)
        this.categoryTableColumnsConfig.find(item => item.property === 'is_active').options = actives
      }
    }
  },
  computed: {
    ...mapState('category', ['categories', 'settings']),
    dataMissing () {
      return this.isDataFetched && !this.categories.length
    },
    changeCategoryModalProps: function () {
      const changeCategoryModalProps = {
        width: 690
      }
      return { ...baseModalProps, ...changeCategoryModalProps }
    },
    hasPagination () {
      return (this.isDataFetched && !this.isDataUpdating) && this.totalItems > this.pageItemsLimit
    },
    paginatedCategories () {
      let categories = _.cloneDeep(this.categories)
      const { page_from = 0, page_to = 100 } = this.filterParams
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.totalItems = categories.length
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.filteredCategories = categories
      return categories.slice(page_from, page_to)
    }
  },
  methods: {
    ...mapActions('category', ['getCategoryData', 'createCategory', 'updateCategory', 'getCategorySettings', 'removeCategory']),
    async loadData () {
      this.isDataFetched && (this.isDataUpdating = true)
      await Promise.all([
        this.getCategoryData({ filters: this.filterParams }),
        this.getCategorySettings()
      ])
      this.isDataUpdating = false
      this.isDataFetched = true
    },
    /**
     * Change page of URLs (pagination changes)
     */
    pageChanged ({ from, to }) {
      this.filterParams.page_from = from
      this.filterParams.page_to = to
    },
    async createOrUpdateCategory (category, isRemove = false) {
      this.isDataUpdating = true
      try {
        if (isRemove) {
          await this.removeCategory({ id: category })
        } else if (category.id && category.id > -1) {
          await this.updateCategory(category)
        } else {
          await this.createCategory(category)
        }
        await this.loadData()
        this.closeCreateModal()
      } catch (e) {
        this.$refs['create-category'].showErrors(e[0])
      } finally {
        this.isDataUpdating = false
      }
    },
    closeCreateModal () {
      this.selectedCategory = null
      this.$modal.hide('create')
    },
    openCreateModal (categoryId) {
      this.selectedCategory = this.categories.find(category => category.id === categoryId)
      this.$modal.show('create')
    },
    handleLimitChange: function (value) {
      this.pageItemsLimit = value
      this.filterParams = { ...this.filterParams, page_from: 0, page_to: value }
    },
    filterColumn (event, column) {
      this.filterParams[column.property] = event
      this.filterParams.page_from = 0
      this.filterParams.page_to = this.pageItemsLimit
      this.loadData()
    },
    onHeaderCellClicked (cell, direction, declineCall) {
      if (direction === 'clear') {
        this.sortConfig.shift()
        return
      }
      if (this.sortConfig.length) {
        this.sortConfig[0].propertyName = cell.property
        this.sortConfig[0].sortDirection = direction
      } else if (cell) {
        this.sortConfig = [{
          sortDirection: direction,
          propertyName: cell.property
        }]
      }
      this.filterParams = {
        ...this.filterParams,
        sort_column: this.sortConfig.map(item => item.propertyName),
        sort_order: this.sortConfig.map(item => item.sortDirection)
      }
      !declineCall && this.loadData()
    },
    cancelSortingAndFilters () {
      const emptyFilters = _.cloneDeep(EMPTY_FILTERS)
      this.filterParams = { ...this.filterParams, ...emptyFilters, page_to: this.pageItemsLimit }
      Object.keys(this.sortableHeaderRefs)
        .forEach(ref => {
          const [element] = this.$refs[ref]
          element.$refs[element.childRef].cancel(true)
        })
      this.sortConfig = []
      !this.isDataUpdating && this.loadData()
    },
    cancelFilter (event, column) {
      this.filterParams[column.property] = null
      this.loadData()
    }
  }
}
</script>

<style scoped lang="scss">

.common-table {
  border-radius: $rfx-default-border-radius;
  overflow: visible;

  &__header {
    background-color: rgba(1, 22, 39, 0.1);
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;
  }

  &__header-cell {
    color: $rfx-dark-march;
    font-weight: bold;
    padding: 0;
    height: 60px;
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  &__cell {
    color: $rfx-dark-march;
    height: 60px;
    padding: 0;
    display: flex;
    align-items: center;
    font-size: 14px;
    border-bottom: 1px solid $rfx-light-steel;

    &:first-child {
      padding-left: 25px;
    }

    &:last-child {
      padding-right: 25px;
    }
  }

  &__row {
    border-left: 1px solid $rfx-light-steel;
    border-right: 1px solid $rfx-light-steel;
  }
}

.category {
  position: relative;
  min-height: 200vh !important;

  &__row {
    &_name {
      max-width: 21%;
    }
  }

  &__truncate {
    width: 90%;
  }

  &__cross {
    font-size: 21px;
    margin-left: 5px;
    margin-top: -2px;
  }

  &__title-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1 1 50%;
    height: 60px;
  }

  &__title-right > *:not(:last-child) {
    margin-right: 30px;
  }

  &__title-left {
    display: flex;
    align-items: center;
  }

  &__title-left > *:not(:last-child) {
    margin-right: 30px;
  }

  &__header-button-wrapper {
    border-radius: 15px;
    border: solid 1px $rfx-light-steel;
    background: white;
    height: 28px;
    display: flex;
    padding: 0 10px;
  }

  &__copy-button {
    flex: 1 1 50%;
    height: 100%;
    border: none;
    outline: none;
    color: $rfx-dark-march;
    white-space: nowrap;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 90px;
    background-color: #fff;
    font-size: 11px;

    &:hover {
      opacity: 0.5;
    }
  }

  &__clear-filters {
    @extend .category__copy-button;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: auto;

    span:first-child {
      font-size: 11px;
    }
  }

  &__grid-select {
    flex-basis: 100px;
  }

  &__title-container {
    padding: 14px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__loader {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    height: calc(100vh - 530px);
  }

  &__table {
    position: relative;
    display: flex;
    flex-direction: column;
  }

  &__update-loader {
    position: absolute;
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    background-color: white;
    top: 0;
    opacity: 0.5;
    z-index: 10000;
  }

  &__update-loader-ring {
    margin-top: 200px;
  }

  &__categories {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
  }

  &__is_active {
    &.common-table__header-cell {
      flex: 0 0 23.3%;
    }
  }

  &__edit-button {
    flex: 0 0 45px;

    &.common-table__header-cell {
      flex-basis: 0;
    }
  }

  &__name {

    &.common-table__header-cell {
      flex-basis: 3.3%;

      &::v-deep .multi-select__title-container {
        padding-left: 68px;
      }
    }
  }

  &__fk_pivot_field,
  &__fk_pivot_table {
    flex: 0 0 26.3%;
  }

  &__button {
    width: 136px;
    padding: 0 20px;
    height: 60px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    border: none;
    outline: none;
    cursor: pointer;
    background-color: $rfx-medium-march;
    color: #fff;
  }

  &__count {
    color: #b2b9be;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    width: 75px;
    white-space: nowrap;
  }
}

.pagination-wrapper {
  display: flex;
  height: 60px;
  margin-top: 10px;
  justify-content: center;
  align-items: center;
  border: 1px solid $rfx-light-steel;
  border-radius: $rfx-default-border-radius;
}

.no-data-block {
  border-bottom: 1px solid $rfx-light-steel;
}

.common-table {
  &__header-cell {
    color: $rfx-dark-march;
    font-weight: bold;
  }
}

.edit-icon {
  cursor: pointer;
  font-size: 20px;
  justify-self: end;
  display: grid;
  color: $rfx-dark-march;
}

</style>
